import {cn, cva} from 'platform/shared';

import {ComponentPropsWithoutRef, ForwardedRef, forwardRef} from 'react';

export type ImageResizeCorner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

const imageResizeOverlayVariants = cva(
  'pointer-events-none absolute z-[5] border border-dashed border-[var(--color-general-accent)]'
);

// The selection outline drawn over the currently selected image. Its position/size are set
// imperatively (via a ref) because they update on every scroll/drag frame. `pointer-events: none`
// lets clicks fall through to the image itself (keeping it selected) — only the corner handles
// capture pointer events for dragging.
export const ImageResizeOverlay = forwardRef(ImageResizeOverlayComponent);
function ImageResizeOverlayComponent(
  props: ComponentPropsWithoutRef<'div'>,
  ref: ForwardedRef<HTMLDivElement>
) {
  return <div {...props} ref={ref} className={cn(imageResizeOverlayVariants(), props.className)} />;
}

const imageMoveIndicatorVariants = cva(
  'pointer-events-none absolute z-[6] w-0 border border-solid border-[var(--color-general-accent)]'
);

// The insertion caret shown while dragging an image to a new position. Top/left/height are set
// imperatively as the cursor moves. A left border draws the 2px bar (px is allowed in borders).
export const ImageMoveIndicator = forwardRef(ImageMoveIndicatorComponent);
function ImageMoveIndicatorComponent(
  props: ComponentPropsWithoutRef<'div'>,
  ref: ForwardedRef<HTMLDivElement>
) {
  return <div {...props} ref={ref} className={cn(imageMoveIndicatorVariants(), props.className)} />;
}

export type ImageResizeHandleProps = ComponentPropsWithoutRef<'div'> & {
  $corner: ImageResizeCorner;
};

const imageResizeHandleVariants = cva(
  'pointer-events-auto absolute size-3 rounded-full border border-solid border-[var(--color-palettes-white-10-100)] bg-[var(--color-general-accent)]',
  {
    variants: {
      corner: {
        topLeft: '-top-1.5 -left-1.5 cursor-nwse-resize',
        topRight: '-top-1.5 -right-1.5 cursor-nesw-resize',
        bottomLeft: '-bottom-1.5 -left-1.5 cursor-nesw-resize',
        bottomRight: '-right-1.5 -bottom-1.5 cursor-nwse-resize',
      },
    },
  }
);

// A draggable corner dot. Each side is selected from the corner name so the four handles share
// one component.
export function ImageResizeHandle(props: ImageResizeHandleProps) {
  const {$corner, className, ...htmlProps} = props;

  return (
    <div {...htmlProps} className={cn(imageResizeHandleVariants({corner: $corner}), className)} />
  );
}
